import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MessageCircle, Clock, CheckCircle, Sparkles } from 'lucide-react';
import { services } from '../mock';
import { Fireflies, Moth } from '../components/FairyElements';

const Services = () => {
  const whatsappNumber = '9681424041';
  const whatsappLink = `https://wa.me/91${whatsappNumber}`;

  return (
    <div className="min-h-screen bg-[#0a1612]">
      <section className="forest-bg min-h-[500px] flex items-center px-4 md:px-8 lg:px-16 py-20" style={{ '--forest-image': "url(/mystical-garden/services-grove.png)", '--bg-opacity': 0.6}}>
        <Fireflies count={40} />
        <Moth delay={0} top="30%" />

        <div className="max-w-6xl mx-auto w-full text-center relative z-10 animate-fadeIn">
          <Badge className="mb-4 bg-emerald-900/60 border border-amber-500/40 text-amber-300 backdrop-blur-md">
            <Sparkles className="w-3 h-3 mr-1.5" />
            <span className="tracking-widest text-xs uppercase">Sacred Offerings</span>
          </Badge>
          <h1 className="text-6xl md:text-7xl font-bold mb-6 gradient-text text-glow">Our Services</h1>
          <p className="text-lg md:text-xl text-amber-100/80 max-w-3xl mx-auto font-serif-mystical italic">
            Choose from our range of spiritual guidance services designed to help you navigate life's journey with clarity and confidence.
          </p>
        </div>
      </section>

      <section className="section-deep py-24 px-4 md:px-8 lg:px-16 relative">
        <Fireflies count={25} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="forest-card border-0 group" data-testid={`service-card-${service.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <CardTitle className="text-2xl mb-2 text-amber-200 group-hover:text-amber-300 transition-colors" style={{ fontFamily: 'Cinzel, serif' }}>
                        {service.title}
                      </CardTitle>
                      <div className="flex items-center gap-2 text-amber-100/60">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">{service.duration}</span>
                      </div>
                    </div>
                    {service.popular && (
                      <Badge className="bg-amber-600/90 text-emerald-950 border-0 font-semibold">Most Popular</Badge>
                    )}
                  </div>
                  <CardDescription className="text-amber-100/70 leading-relaxed">{service.description}</CardDescription>
                </CardHeader>

                <CardContent>
                  <div className="mb-6 p-4 bg-emerald-900/40 rounded-lg border border-amber-600/20">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      {service.discountedPrice ? (
                        <>
                          <span className="text-4xl font-bold text-amber-400">{service.currency}{service.discountedPrice}</span>
                          <span className="text-2xl text-amber-100/40 line-through">{service.currency}{service.price}</span>
                          <Badge className="bg-emerald-700 text-amber-300 border border-amber-500/30">
                            Save {service.currency}{service.price - service.discountedPrice}
                          </Badge>
                        </>
                      ) : service.price ? (
                        <span className="text-4xl font-bold text-amber-400">{service.currency}{service.price}</span>
                      ) : (
                        <span className="text-2xl font-semibold text-amber-400">Contact for Pricing</span>
                      )}
                    </div>
                    {service.availability && (
                      <p className="text-sm text-amber-100/60 mt-2">
                        <span className="font-medium text-amber-300">Availability:</span> {service.availability}
                      </p>
                    )}
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-amber-300 mb-3 uppercase tracking-wider text-sm" style={{ fontFamily: 'Cinzel, serif' }}>
                      What's Included
                    </h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-amber-100/80">
                          <CheckCircle className="w-5 h-5 mr-3 text-emerald-400 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button
                    className="btn-gold w-full py-6 text-base"
                    onClick={() => window.open(whatsappLink, '_blank')}
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Book via WhatsApp
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="forest-bg py-24 px-4 md:px-8 lg:px-16 relative" style={{ '--forest-image': "url(/mystical-garden/hero-forest.png)", '--bg-opacity': 0.3}}>
        <Fireflies count={20} />
        <div className="max-w-5xl mx-auto relative z-10">
          <h2 className="text-5xl font-bold text-center mb-16 gradient-text">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { num: '1', title: 'Choose Your Service', desc: 'Select the reading or guidance package that resonates with your current needs.' },
              { num: '2', title: 'Book via WhatsApp', desc: 'Contact us on WhatsApp to schedule your session at a convenient time.' },
              { num: '3', title: 'Receive Guidance', desc: 'Experience your personalized reading and gain the clarity you seek.' }
            ].map((step, idx) => (
              <div key={idx} className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-700 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-5 mystical-glow-gold border border-amber-500/40">
                  <span className="text-3xl font-bold text-amber-100" style={{ fontFamily: 'Cinzel, serif' }}>{step.num}</span>
                </div>
                <h3 className="text-2xl font-semibold mb-3 text-amber-200" style={{ fontFamily: 'Cinzel, serif' }}>{step.title}</h3>
                <p className="text-amber-100/70">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="forest-bg py-24 px-4 md:px-8 lg:px-16 relative" style={{ '--forest-image': "url(/mystical-garden/hero-forest.png)", '--bg-opacity': 0.5}}>
        <Fireflies count={40} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">Not Sure Which to Choose?</h2>
          <p className="text-lg mb-8 text-amber-100/80 font-serif-mystical italic">
            Contact me on WhatsApp and I'll help you find the perfect guidance for your journey.
          </p>
          <Button size="lg" className="btn-gold px-10 py-7" onClick={() => window.open(whatsappLink, '_blank')}>
            <MessageCircle className="mr-2 h-5 w-5" />
            Chat on WhatsApp
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Services;
